import { Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IDispositivoAlarma, IQueryParam } from 'modelos/src';
import { NgSelectModule } from '@ng-select/ng-select';
import { AlarmasDomiciliariasService } from '../../../modulos/entidades/alarmas/alarmas-domiciliarias/alarmas-domiciliarias.service';

@Component({
  selector: 'app-alarma-selector',
  templateUrl: './alarma-selector.component.html',
  styleUrl: './alarma-selector.component.scss',
  standalone: true,
  imports: [FormsModule, NgSelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AlarmaSelectorComponent),
      multi: true,
    },
  ],
})
export class AlarmaSelectorComponent implements ControlValueAccessor, OnChanges {
  private alarmasService = inject(AlarmasDomiciliariasService);

  @Input() idCliente: string | null = null;
  @Input() placeholder = 'Buscar alarma';
  @Input() disabled = false;
  @Input() clearable = true;
  @Input() includeChildren = true;

  @Output() alarmaChange = new EventEmitter<IDispositivoAlarma | null>();
  @Output() alarmaIdChange = new EventEmitter<string | null>();

  public alarmas: IDispositivoAlarma[] = [];
  public loading = false;
  public innerValue: IDispositivoAlarma | null = null;

  private onChange: (id: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  public alarmaLabel(alarma: IDispositivoAlarma): string {
    const partes = [alarma.numeroAbonado, alarma.nombre].filter(Boolean);
    return partes.join(' - ') || alarma._id || '';
  }

  public onAlarmaChange(alarma: IDispositivoAlarma | null): void {
    this.innerValue = alarma;
    this.onChange(alarma?._id ?? null);
    this.onTouched();
    this.alarmaChange.emit(alarma);
    this.alarmaIdChange.emit(alarma?._id ?? null);
  }

  private async cargarAlarmas(): Promise<void> {
    this.loading = true;
    try {
      const query: IQueryParam = {
        filter: JSON.stringify({ idCliente: this.idCliente }),
        includeChildren: this.includeChildren,
        sort: 'numeroAbonado',
        select: '_id numeroAbonado nombre',
      };
      const result = await this.alarmasService.getFilteredSinLimite(query);
      this.alarmas = result.datos;
    } catch (err) {
      console.error('Error cargando alarmas:', err);
      this.alarmas = [];
    } finally {
      this.loading = false;
    }
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['idCliente']) {
      this.innerValue = null;
      this.onChange(null);
      this.alarmaChange.emit(null);
      this.alarmaIdChange.emit(null);
      this.alarmas = [];
      if (this.idCliente) {
        await this.cargarAlarmas();
      }
    }
  }

  // ControlValueAccessor
  writeValue(id: string | null): void {
    if (id && this.alarmas.length) {
      this.innerValue = this.alarmas.find((a) => a._id === id) ?? null;
    } else {
      this.innerValue = null;
    }
  }

  registerOnChange(fn: (id: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
